import React, { useEffect, useRef } from 'react';

interface SmokeOverlayProps {
  colors?: {
    primary?: string;
    secondary?: string;
  };
  opacity?: number;
}

export default function SmokeOverlay({ colors, opacity = 0.15 }: SmokeOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: SmokeParticle[] = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [10, 116, 255];
      // If already rgb/rgba
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      // Hex #RGB or #RRGGBB
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [10, 116, 255];
      const r = parseInt(c.slice(0, 2), 16);
      const g = parseInt(c.slice(2, 4), 16);
      const b = parseInt(c.slice(4, 6), 16);
      return [r, g, b];
    };

    // Will be updated every frame from CSS variable
    let accentRgb: [number, number, number] = [10, 116, 255];

    class SmokeParticle {
      x: number;
      y: number;
      size: number;
      speedY: number;
      speedX: number;
      opacity: number;
      life: number;
      maxLife: number;

      constructor(initialSpawn = false) {
        this.x = 0;
        this.y = 0;
        this.size = 0;
        this.speedY = 0;
        this.speedX = 0;
        this.opacity = 0;
        this.life = 0;
        this.maxLife = 0;
        this.reset(initialSpawn);
      }

      reset(initialSpawn = false) {
        this.x = Math.random() * canvas.width;
        if (initialSpawn) {
          this.y = Math.random() * canvas.height;
        } else {
          this.y = canvas.height + Math.random() * 100;
        }
        this.size = Math.random() * 150 + 100;
        this.speedY = Math.random() * 0.5 + 0.2;
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.opacity = Math.random() * 0.3 + 0.1;
        this.life = 0;
        this.maxLife = Math.random() * 200 + 100;
      }

      update() {
        this.y -= this.speedY;
        this.x += this.speedX;
        this.life++;
        
        if (this.life < 50) {
          this.opacity = (this.life / 50) * (Math.random() * 0.3 + 0.1);
        } else if (this.life > this.maxLife - 50) {
          this.opacity = ((this.maxLife - this.life) / 50) * (Math.random() * 0.3 + 0.1);
        }

        if (this.y < -this.size || this.life > this.maxLife) {
          this.reset();
        }
      }

      draw() {
        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, this.size
        );
        
        const [r, g, b] = accentRgb;
        const a1 = Math.max(0, Math.min(1, this.opacity));
        const a2 = Math.max(0, Math.min(1, this.opacity * 0.5));
        gradient.addColorStop(0, `rgba(${r},${g},${b},${a1})`);
        gradient.addColorStop(0.5, `rgba(${r},${g},${b},${a2})`);
        gradient.addColorStop(1, 'transparent');

        ctx.fillStyle = gradient;
        ctx.fillRect(this.x - this.size, this.y - this.size, this.size * 2, this.size * 2);
      }
    }

    for (let i = 0; i < 20; i++) {
      particles.push(new SmokeParticle(true));
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Read header accent color each frame to respond to theme/accent changes
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF';
      accentRgb = parseCssColorToRgb(accent);

      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [colors, opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
